import { Auth0ContextInterface, useAuth0 } from '@auth0/auth0-react';
import { useLogger } from '@opengov/psc-react-logger';
import { jwtDecode } from 'jwt-decode';
import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';

import { pickAuth0Config } from '../../TopLevelNavigation/loggerSessionContext';
import { IAuth0Config } from '../../types';

/**
 * Auth0 Context
 *
 * This context is created to localize the scope of Auth0's context specifically for the nav bar component.
 * It is particularly useful in scenarios where the nav bar component is used within a React application
 * that is wrapped in additional parent Auth0Provider instances. By using this localized context, we ensure
 * that the nav bar component can manage its own Auth0 state independently, avoiding potential conflicts
 * or unintended behavior caused by multiple Auth0Provider instances in the parent components.
 *
 * [Auth0 Context Documentation](https://auth0.github.io/auth0-react/interfaces/Auth0ProviderOptions.html#context)
 *
 * @type {React.Context<null>}
 */
export const navAuth0Context = createContext<Auth0ContextInterface | null>(null);

/**
 * Whether the host configured an Auth0 client: a domain and client id. Without one the nav bar
 * gets a placeholder session in place of a real client, which has no token to give.
 */
export const hasAuth0Client = (auth0Config?: { auth0domain?: string; auth0clientID?: string }): boolean =>
  !!(auth0Config?.auth0domain && auth0Config?.auth0clientID);

/**
 * Nav Auth context interface
 */
interface NavAuthContextType {
  auth0Config?: IAuth0Config;
  authBearerToken?: string;
  getAccessToken?: () => Promise<string>;
  isAuthenticated: boolean;
  isLoading: boolean;
  error?: Error;
  forceRefresh: () => Promise<void>;
}

/**
 * Nav Auth context
 */
const NavAuthContext = React.createContext<NavAuthContextType | undefined>(undefined);

/**
 * Nav Auth provider props
 */
interface NavAuthProviderProps {
  auth0Config?: IAuth0Config;
  children: React.ReactNode;
}

/**
 * Provider component that provides Nav Auth configuration and manages token state
 */
export const NavAuthProvider: React.FC<NavAuthProviderProps> = ({ auth0Config, children }) => {
  const logger = useLogger();

  const {
    isAuthenticated,
    isLoading: isLoadingAuth,
    getAccessTokenSilently,
    error: auth0error
  } = useAuth0(navAuth0Context);

  const {
    auth0token: propsAuth0token,
    auth0GetAccessToken: propsGetAccessToken,
    auth0callbackUrl,
    auth0clientID,
    auth0domain,
    onAuth0SessionExpired
  } = auth0Config || {};

  const hasAuth0Config = !!(auth0clientID && auth0domain && auth0callbackUrl);
  const isMounted = useRef(true);
  const [authBearerToken, setAuthBearerToken] = useState(propsAuth0token);

  useEffect(() => {
    if (hasAuth0Client(auth0Config) && !isLoadingAuth && !isAuthenticated) {
      logger.info(
        'Auth0 config provided but session validation failed. Will attempt silent token refresh. ' +
          'If refresh fails with login_required error and onAuth0SessionExpired callback exists, ' +
          'the callback will be triggered to handle re-authentication.',
        {
          auth0Config: pickAuth0Config(auth0Config),
          isAuthenticated
        }
      );

      getAccessTokenSilently({ cacheMode: 'off' })
        .then((token) => {
          setAuthBearerToken(token);
          logger.info('Session successfully refreshed with new access token');
        })
        .catch((error) => {
          if (error.error === 'login_required' && !onAuth0SessionExpired) {
            logger.error(
              'Auth0 login required but no onAuth0SessionExpired handler provided - authentication cannot be handled automatically.',
              { error }
            );
          } else if (error.error === 'login_required' && onAuth0SessionExpired) {
            logger.info('Auth0 login required - triggering onAuth0SessionExpired callback');
            onAuth0SessionExpired();
          } else {
            logger.error('Failed to refresh session: Error retrieving access token', { error });
          }
        });
    }
  }, [auth0Config, isLoadingAuth, isAuthenticated, logger, onAuth0SessionExpired]);

  useEffect(() => {
    if (auth0error) {
      logger.error('Auth0 authentication error', {
        error: auth0error,
        isAuthenticated
      });
    }
  }, [auth0error, isAuthenticated, logger]);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  // Retrieve the auth0 access token from the cache or fetch a new one
  // Sets the token into state once fetched
  // Logs errors if the token fetch fails
  const getAccessToken = ({ disableCacheFetch = false }: { disableCacheFetch?: boolean }) => {
    // Requesting an access token before the expiry time will return the cached token
    // Turning off the cache fetch forces a new token to be fetched instead
    logger.info('Attempting to get access token', {
      disableCacheFetch,
      isAuthenticated
    });

    getAccessTokenSilently({ cacheMode: disableCacheFetch ? 'off' : 'on' })
      .then((accessToken) => {
        setAuthBearerToken(accessToken);
      })
      .catch((error) => {
        if (error.error === 'login_required' && onAuth0SessionExpired) {
          logger.error('User session expired, invoking callback', {
            error
          });
          onAuth0SessionExpired();
        } else if (error.error === 'login_required' && !onAuth0SessionExpired) {
          logger.error('Auth0 session expired but no onAuth0SessionExpired handler provided', {
            error
          });
        } else {
          logger.error('Failed to fetch access token', {
            error
          });
        }
      });
  };

  // Automatically fetches a new access token before the current one expires
  // Decodes the access token to determine the expiration time
  // If the access token is unable to be decoded, background token renewal is disabled and an error is logged
  useEffect(() => {
    // Be sure to wrap this in a try/catch block
    // This check could fail if the token is not a valid JWT, or if it is encrypted
    const getMillisecondsUntilTokenExpiration = (token: string) => {
      const decodedToken = jwtDecode(token);
      const expirationTime = (decodedToken?.exp || 0) * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiration = expirationTime - currentTime;

      return timeUntilExpiration;
    };

    let tokenRenewalTimeout: NodeJS.Timeout;
    // Checks for auth0 config since the token renewal is not possible without it
    // Do not proceed if the token is already provided in the props, since in that case we are not responsible for renewing it
    if (authBearerToken && hasAuth0Config && !propsAuth0token) {
      try {
        // Set the refresh timeout to fire 1 minute before token expiration
        const millisecondsUntilTokenRefresh = getMillisecondsUntilTokenExpiration(authBearerToken) - 60_000;
        if (millisecondsUntilTokenRefresh > 0) {
          tokenRenewalTimeout = setTimeout(() => {
            logger.info('Access token is about to expire. Attempting to fetch a new one using auth0config.');
            getAccessToken({ disableCacheFetch: true });
          }, millisecondsUntilTokenRefresh);
        } else {
          logger.info('Access token has already expired. Attempting to fetch a new one using auth0config.');
          getAccessToken({ disableCacheFetch: true });
        }
      } catch (error) {
        logger.error(
          'Error parsing auth0 token. This is commonly the result of a missing or mismatched configuration setting the Auth0 "audience":',
          {
            error
          }
        );
      }
    }

    // Check the provided auth0 token for expiration
    // We won't be able to renew the token if it's already expired,
    // But we can still log a console warning
    if (propsAuth0token) {
      try {
        const millisecondsUntilTokenExpiration = getMillisecondsUntilTokenExpiration(propsAuth0token);

        if (millisecondsUntilTokenExpiration > 0) {
          tokenRenewalTimeout = setTimeout(() => {
            logger.warn(
              'The provided auth0 access token is expired. Platform data cannot be fetched without a valid token.'
            );
          }, millisecondsUntilTokenExpiration);
        } else {
          logger.warn(
            'The provided auth0 access token has already expired. Platform data cannot be fetched without a valid token.'
          );
        }
      } catch (error) {
        logger.error(
          'Error parsing provided auth0 token. This is commonly the result of a missing or mismatched configuration setting the Auth0 "audience":',
          {
            error
          }
        );
      }
    }
    return () => {
      if (tokenRenewalTimeout) clearTimeout(tokenRenewalTimeout);
    };
  }, [authBearerToken, hasAuth0Config, propsAuth0token, logger]);

  // Fetch the auth0 access token using the provided configuration
  // This effect handles initial token fetch on the provider's mount
  useEffect(() => {
    if (propsAuth0token && authBearerToken !== propsAuth0token) {
      // If a token is provided in the auth0Config, use it to set the authBearerToken instead of fetching a new one
      setAuthBearerToken(propsAuth0token);
      return;
    }

    // Cannot fetch a token without the auth0Config
    if (!hasAuth0Config) return;
    if (!authBearerToken && !isLoadingAuth && isAuthenticated) {
      // If a cached token is available, this will get it from localStorage
      // If it is not available, this will fetch a new token from auth0
      getAccessToken({ disableCacheFetch: false });
    }
  }, [isAuthenticated, isLoadingAuth, authBearerToken, hasAuth0Config, propsAuth0token]);

  // Force refresh method that handles both provided and fetched tokens
  const forceRefresh = React.useCallback(async () => {
    const isTokenProvided = !!propsAuth0token;

    if (isTokenProvided) {
      logger.warn('Cannot refresh provided token - using existing token');
      return;
    }

    if (!hasAuth0Config) {
      logger.warn('No Auth0 config available for token refresh');
      return;
    }

    // Actually refresh from Auth0
    getAccessToken({ disableCacheFetch: true });
  }, [propsAuth0token, hasAuth0Config, getAccessToken, logger]);

  const resolveAccessToken = useMemo(() => {
    if (propsGetAccessToken) return propsGetAccessToken;
    if (hasAuth0Config) return () => getAccessTokenSilently();
    return undefined;
  }, [propsGetAccessToken, hasAuth0Config, getAccessTokenSilently]);

  const contextValue: NavAuthContextType = useMemo(
    () => ({
      auth0Config,
      authBearerToken,
      getAccessToken: resolveAccessToken,
      isAuthenticated,
      isLoading: isLoadingAuth,
      error: auth0error,
      forceRefresh
    }),
    [auth0Config, authBearerToken, resolveAccessToken, isAuthenticated, isLoadingAuth, auth0error, forceRefresh]
  );

  return <NavAuthContext.Provider value={contextValue}>{children}</NavAuthContext.Provider>;
};

/**
 * Hook to consume Nav Auth context
 */
export const useNavAuth = (): Omit<NavAuthContextType, 'auth0Config'> => {
  const context = React.useContext(NavAuthContext);

  if (context === undefined) {
    throw new Error('useNavAuth must be used within a NavAuthProvider');
  }

  return {
    authBearerToken: context.authBearerToken,
    getAccessToken: context.getAccessToken,
    isAuthenticated: context.isAuthenticated,
    isLoading: context.isLoading,
    error: context.error,
    forceRefresh: context.forceRefresh
  };
};
